import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { logger } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'

export interface EntitiesState {
  eTags: { [id: string]: string | null }
  receivedEntities: Persisted<Entity>[]
}

// Define the initial state using that type
export const initialState: EntitiesState = {
  eTags: {},
  receivedEntities: [],
}

export const entitiesSlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    /** We use eTags to avoid overwriting metadata on Entities. Most editing is through an YDoc, which does not need any sync */
    setEtag: (state, action: PayloadAction<{ id: string; eTag: string }>) => {
      state.eTags[action.payload.id] = action.payload.eTag
    },
    /** Receive the entity from the backend */
    receiveEntity: (state, action: PayloadAction<{ entity: Persisted<Entity>; eTag?: string | null }>) => {
      /** @no-op - handle this action in entitiesApi */
      logger.debug('Received entity', action.payload.entity)
      /**
       * GH #1666 / #1640: this was append-only, so every backend push — including
       * re-receiving the SAME entity on each Performance-mode song switch —
       * appended a fresh full `Persisted<Entity>` (with its document subtree) and
       * never released the old copy. Page-turning a fixed set grew the heap
       * ~0.9MB/round monotonically. The sole consumer (`mergeChatComments`) keys
       * by id and keeps the newest, so collapse duplicates here: drop any prior
       * copy of this id, then append the fresh one. Bounds the array to the
       * distinct-entity count rather than the number of pushes.
       */
      const { entity } = action.payload
      state.receivedEntities = [...state.receivedEntities.filter(e => e.id !== entity.id), entity]
    },
    receivePartialEntity: (state, action: PayloadAction<Partial<Persisted<Entity>> & { id: string }>) => {
      /**
       * @no-op - we handle this in entities-middleware
       */
      logger.debug('Received partial entity', action.payload)
    },
  },
})
